import React from 'react';

import { Fields } from '../generator/types';

import { buildDateFieldHtml } from './dateInput';
import { readFieldValue } from './fieldKeys';
import { resolveFieldToken } from './markerResolver';
import { parse, Token } from './markers';

export interface HtmlMarkerRendererProps {
  text: string;
  fieldPlaceholders: Record<string, string>;
  fieldValues: Record<string, string>;
  riders: string[];
  carrierDisplayName: string;
  onFieldChange: (key: string, value: string) => void;
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function buildParagraphHtml(
  text: string,
  fieldValues: Record<string, string>,
  fieldPlaceholders: Record<string, string>,
  riders: string[],
  carrierDisplayName: string,
  blockId?: string,
): string {
  const tokens = parse(text);
  return tokens
    .map(token => {
      if (token.kind === 'text') return esc(token.value);
      if (token.kind === 'bold') return `<strong>${esc(token.value)}</strong>`;
      if (token.kind === 'link')
        return `<a href="${esc(token.href)}" target="_blank" rel="noopener noreferrer">${esc(token.label)}</a>`;
      // Date fields render outside contentEditable (see buildDateFieldHtml).
      if (token.key === Fields.dateMeasurementTaken) {
        return buildDateFieldHtml(
          token.key,
          readFieldValue(fieldValues, token.key, blockId) ?? '',
        );
      }
      const resolved = resolveFieldToken({
        key: token.key,
        fieldValues,
        fieldPlaceholders,
        riders,
        carrierDisplayName,
        blockId,
      });
      // Reserved fields (riders, carrierDisplayName) render inline, not editable.
      if (token.key === 'riders' || token.key === 'carrierDisplayName') {
        return esc(resolved.value);
      }
      const display = resolved.isFilled ? resolved.value : resolved.placeholder;
      return (
        `<span data-field="${esc(token.key)}" data-placeholder="${esc(resolved.placeholder)}" tabindex="0" style="display:inline-block;` +
        `background:#FFFBEB;` +
        `color:${resolved.isFilled ? '#1A1917' : '#92400E'};` +
        `border:1px solid #FDE68A;` +
        `border-radius:3px;` +
        `padding:0 4px;` +
        `font-size:11px;` +
        `font-family:monospace;` +
        `min-width:60px;">${esc(display)}</span>`
      );
    })
    .join('');
}

export function resolveToPlainText(
  text: string,
  fieldValues: Record<string, string>,
  fieldPlaceholders: Record<string, string>,
  riders: string[],
  carrierDisplayName: string,
  blockId?: string,
): string {
  const tokens = parse(text);
  return tokens
    .map(token => {
      if (token.kind === 'text') return token.value;
      if (token.kind === 'bold') return token.value;
      if (token.kind === 'link') return token.label;
      const resolved = resolveFieldToken({
        key: token.key,
        fieldValues,
        fieldPlaceholders,
        riders,
        carrierDisplayName,
        blockId,
      });
      return resolved.isFilled ? resolved.value : `[${resolved.placeholder}]`;
    })
    .join('');
}

export const HtmlMarkerRenderer = ({
  text,
  fieldPlaceholders,
  fieldValues,
  riders,
  carrierDisplayName,
  onFieldChange,
}: HtmlMarkerRendererProps) => {
  const tokens = parse(text);
  return (
    <>
      {tokens.map((token, idx) =>
        renderToken(token, idx, {
          fieldPlaceholders,
          fieldValues,
          riders,
          carrierDisplayName,
          onFieldChange,
        }),
      )}
    </>
  );
};

interface RenderContext {
  fieldPlaceholders: Record<string, string>;
  fieldValues: Record<string, string>;
  riders: string[];
  carrierDisplayName: string;
  onFieldChange: (key: string, value: string) => void;
}

function renderToken(
  token: Token,
  idx: number,
  ctx: RenderContext,
): React.ReactNode {
  if (token.kind === 'text') {
    return <React.Fragment key={idx}>{token.value}</React.Fragment>;
  }
  if (token.kind === 'bold') {
    return <strong key={idx}>{token.value}</strong>;
  }
  if (token.kind === 'link') {
    return (
      <a key={idx} href={token.href} target="_blank" rel="noopener noreferrer">
        {token.label}
      </a>
    );
  }
  const resolved = resolveFieldToken({
    key: token.key,
    fieldValues: ctx.fieldValues,
    fieldPlaceholders: ctx.fieldPlaceholders,
    riders: ctx.riders,
    carrierDisplayName: ctx.carrierDisplayName,
  });
  // Reserved fields render inline as plain text (not editable).
  if (token.key === 'riders' || token.key === 'carrierDisplayName') {
    return <React.Fragment key={idx}>{resolved.value}</React.Fragment>;
  }
  return (
    <EditableField
      key={idx}
      fieldKey={token.key}
      placeholder={resolved.placeholder}
      value={resolved.isFilled ? resolved.value : ''}
      onChange={ctx.onFieldChange}
    />
  );
}

interface EditableFieldProps {
  fieldKey: string;
  placeholder: string;
  value: string;
  onChange: (key: string, value: string) => void;
}

const EditableField = ({
  fieldKey,
  placeholder,
  value,
  onChange,
}: EditableFieldProps) => {
  const ref = React.useRef<HTMLSpanElement>(null);

  // Seed once on mount; React must NOT re-render the editable text node, or
  // the cursor resets to position 0 on every keystroke.
  React.useEffect(() => {
    if (ref.current) {
      ref.current.textContent = value ?? '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value changes only when the field is NOT focused, so
  // user typing is never disturbed.
  React.useEffect(() => {
    if (!ref.current) return;
    if (document.activeElement === ref.current) return;
    if (ref.current.textContent !== (value ?? '')) {
      ref.current.textContent = value ?? '';
    }
  }, [value]);

  const isEmpty = !value || value.trim() === '';

  return (
    <span
      style={{
        display: 'inline-grid',
        verticalAlign: 'baseline',
      }}
    >
      <span
        ref={ref}
        data-field={fieldKey}
        data-empty={isEmpty ? 'true' : 'false'}
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        title={isEmpty ? `Required: ${placeholder}` : undefined}
        onInput={e => {
          const text = (e.currentTarget as HTMLSpanElement).textContent ?? '';
          onChange(fieldKey, text);
        }}
        style={{
          gridArea: '1/1',
          display: 'block',
          background: isEmpty ? '#FEF2F2' : 'transparent',
          color: isEmpty ? '#B91C1C' : 'inherit',
          border: `1px solid ${isEmpty ? '#FCA5A5' : '#FDE68A'}`,
          borderRadius: 3,
          padding: '0 4px',
          minWidth: 60,
          outline: 'none',
          fontSize: '0.9em',
          fontWeight: isEmpty ? 600 : 400,
          whiteSpace: 'pre-wrap',
        }}
      />
      {isEmpty ? (
        <span
          aria-hidden
          style={{
            gridArea: '1/1',
            pointerEvents: 'none',
            color: '#B91C1C',
            fontWeight: 600,
            fontSize: '0.9em',
            opacity: 0.65,
            padding: '0 4px',
            whiteSpace: 'nowrap',
            userSelect: 'none',
          }}
        >
          {placeholder}
        </span>
      ) : null}
    </span>
  );
};
